import { DataRegion, LocalStorageKeys } from '../types/enums';
import { SourceMapRegistryItem } from '../types/main-content.types';
import {
  MapConfig,
  VisibleCitiesSyncState,
} from '@ag2-weathermaker/features/source-map-container/types/source-map.types';
import {
  DEFAULT_MAP_CONFIG,
  DEFAULT_VISIBLE_CITIES_SYNC_STATE,
  MAIN_MAP_ID,
  MAP_CENTER_BASED_ON_REGION,
} from '@ag2-weathermaker/shared/types/constants';
import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import type { Map as MaplibreMap } from 'maplibre-gl';

@Injectable({
  providedIn: 'root',
})
export class MapsService {
  private mainMap: MaplibreMap | null = null;
  private sourceMapList: SourceMapRegistryItem[] = [];
  private mapConfigSubject = new BehaviorSubject<MapConfig>(this.getInitialMapConfig());
  private visibleCitiesSyncStateSubject = new BehaviorSubject<VisibleCitiesSyncState>(
    DEFAULT_VISIBLE_CITIES_SYNC_STATE
  );

  private loadingMapsSubject = new BehaviorSubject<Record<string, boolean>>({ [MAIN_MAP_ID]: true });
  public loadingMaps$ = this.loadingMapsSubject.asObservable();

  public disabledButtonsSignal = signal<boolean>(false);
  public disabledPolygonsSignal = signal<boolean>(false);

  // only required on first app initialization
  private refreshMainMapContour$ = new Subject<void>();

  private mainMapInteraction$ = new Subject<boolean>();

  getRefreshMainMapContour$(): Observable<void> {
    return this.refreshMainMapContour$.asObservable();
  }

  triggerRefreshMainMapContour(): void {
    this.refreshMainMapContour$.next();
  }

  getMainMapInteraction$(): Observable<boolean> {
    return this.mainMapInteraction$.asObservable();
  }

  setMainMapInteraction(enabled: boolean): void {
    this.mainMapInteraction$.next(enabled);
  }

  setMainMap(map: MaplibreMap | null): void {
    this.mainMap = map;
  }

  getMainMap(): MaplibreMap | null {
    return this.mainMap;
  }

  addSourceMap(map: SourceMapRegistryItem): void {
    this.sourceMapList.push(map);
  }

  deleteSourceMap(mapId: string): void {
    this.sourceMapList = this.sourceMapList.filter((map) => map.mapId !== mapId);
  }

  getMapConfig$(): Observable<MapConfig> {
    return this.mapConfigSubject.asObservable();
  }

  getMapConfig(): MapConfig {
    return this.mapConfigSubject.getValue();
  }

  setMapConfig(value: MapConfig): void {
    this.mapConfigSubject.next(value);
  }

  reinitializeMapConfigForRegion(region: DataRegion): void {
    const center = MAP_CENTER_BASED_ON_REGION[region];
    this.mapConfigSubject.next({ ...DEFAULT_MAP_CONFIG, center });
  }

  getVisibleCitiesSyncState$(): Observable<VisibleCitiesSyncState> {
    return this.visibleCitiesSyncStateSubject.asObservable();
  }

  getVisibleCitiesSyncState(): VisibleCitiesSyncState {
    return this.visibleCitiesSyncStateSubject.getValue();
  }

  setVisibleCitiesSyncState(value: VisibleCitiesSyncState): void {
    this.visibleCitiesSyncStateSubject.next(value);
  }

  private getLoadingMaps(): Record<string, boolean> {
    return this.loadingMapsSubject.getValue();
  }

  updateLoadingMaps(sourceId: string, value: boolean): void {
    const maps = this.getLoadingMaps();
    maps[sourceId] = value;
    this.loadingMapsSubject.next(maps);
  }

  changeButtonStatus(status: boolean): void {
    this.disabledButtonsSignal.set(status);
  }

  changePolygonStatus(status: boolean): void {
    this.disabledPolygonsSignal.set(status);
  }

  setMapPointerEvents(map: MaplibreMap | null, enabled: boolean): void {
    const canvas = map?.getCanvas();
    if (canvas) {
      canvas.style.pointerEvents = enabled ? 'auto' : 'none';
    }
  }

  private getInitialMapConfig(): MapConfig {
    const storedConfig = localStorage.getItem(LocalStorageKeys.mapConfigs);
    if (storedConfig) {
      const parsed = JSON.parse(storedConfig) as MapConfig;
      if (parsed?.center && parsed?.zoom != null) {
        return { ...DEFAULT_MAP_CONFIG, center: parsed.center, zoom: parsed.zoom };
      }
    }

    const defaultRegion = localStorage.getItem(LocalStorageKeys.defaultRegion) as DataRegion | null;
    if (!defaultRegion) {
      return DEFAULT_MAP_CONFIG;
    } else {
      return { ...DEFAULT_MAP_CONFIG, center: MAP_CENTER_BASED_ON_REGION[defaultRegion] };
    }
  }
}
